/**
 * Datenblätter: Ablage in den Einstellungen, Auswahl an Modul und Komponente.
 *
 * Einmal hochladen, beliebig oft verknüpfen — ein Fronius-Datenblatt für zehn
 * Wechselrichter (Wunsch von Mathias, 11/2026).
 */

import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '../daten/db.ts';
import {
  datenblattname,
  entferneDatenblatt,
  ladeDatenblattHoch,
  oeffneDatenblatt,
  useDatenblaetter,
  type Datenblatt,
} from '../daten/datenblaetter.ts';

function groesse(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toLocaleString('de-AT', { maximumFractionDigits: 1 })} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

export function Datenblattablage({ darf }: { darf: boolean }): React.JSX.Element {
  const liste = useDatenblaetter();
  const [datei, setDatei] = useState<File | null>(null);
  const [titel, setTitel] = useState('');
  const [hersteller, setHersteller] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  // Ein Dateifeld lässt sich nicht leeren — nach dem Hochladen wird es neu gezeichnet.
  const [feldNr, setFeldNr] = useState(0);
  const [meldung, setMeldung] = useState<{ text: string; fehler?: boolean } | null>(null);

  // Wo ein Datenblatt verknüpft ist — vor dem Entfernen will man das wissen.
  const verwendung = useLiveQuery(
    async () => {
      const zaehler = new Map<string, number>();
      const zaehle = (id: string | null | undefined): void => {
        if (id) zaehler.set(id, (zaehler.get(id) ?? 0) + 1);
      };
      (await db.module.toArray()).filter((m) => !m.geloescht_am).forEach((m) => zaehle(m.datenblatt_id));
      (await db.komponenten.toArray()).filter((k) => !k.geloescht_am).forEach((k) => zaehle(k.datenblatt_id));
      return zaehler;
    },
    [],
    new Map<string, number>(),
  );

  async function hochladen(): Promise<void> {
    if (!datei) return;
    setLaeuft(true);
    setMeldung(null);
    try {
      await ladeDatenblattHoch(datei, titel.trim() || datei.name.replace(/\.[^.]+$/, ''), hersteller);
      setMeldung({ text: `„${titel.trim() || datei.name}“ ist abgelegt.` });
      setDatei(null);
      setTitel('');
      setFeldNr((n) => n + 1);
    } catch (fehler) {
      setMeldung({ text: fehler instanceof Error ? fehler.message : String(fehler), fehler: true });
    } finally {
      setLaeuft(false);
    }
  }

  async function entferne(d: Datenblatt): Promise<void> {
    const n = verwendung.get(d.id) ?? 0;
    const frage =
      n > 0
        ? `„${datenblattname(d)}“ ist bei ${n} Modul(en) oder Komponente(n) verknüpft. Trotzdem entfernen?`
        : `„${datenblattname(d)}“ entfernen?`;
    if (!window.confirm(frage)) return;
    try {
      await entferneDatenblatt(d.id);
    } catch (fehler) {
      setMeldung({ text: fehler instanceof Error ? fehler.message : String(fehler), fehler: true });
    }
  }

  async function oeffne(d: Datenblatt): Promise<void> {
    try {
      await oeffneDatenblatt(d.id);
    } catch (fehler) {
      setMeldung({ text: fehler instanceof Error ? fehler.message : String(fehler), fehler: true });
    }
  }

  return (
    <section>
      <h2>Datenblätter</h2>
      <p className="hinweis">
        Einmal hochladen, dann bei Modulen und Komponenten auswählen — ein Datenblatt kann für
        beliebig viele Geräte gelten. PDF oder Bild, höchstens 25 MB. Zum Hochladen und Öffnen
        braucht es Verbindung.
      </p>

      {darf && (
        <div className="kachel" style={{ marginBottom: '1rem' }}>
          <div className="feldraster">
            <label>
              Datei
              <input
                key={feldNr}
                type="file"
                accept="application/pdf,image/*"
                disabled={!navigator.onLine}
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setDatei(f);
                  if (f && !titel) setTitel(f.name.replace(/\.[^.]+$/, ''));
                }}
              />
            </label>
            <label>
              Titel
              <input value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="z. B. Symo GEN24 Plus" />
            </label>
            <label>
              Hersteller
              <input value={hersteller} onChange={(e) => setHersteller(e.target.value)} placeholder="z. B. Fronius" />
            </label>
          </div>
          <div className="knopfreihe">
            <button className="wichtig" disabled={!datei || laeuft} onClick={() => void hochladen()}>
              {laeuft ? 'Lädt hoch …' : 'Hochladen'}
            </button>
            {!navigator.onLine && <span className="klein gedaempft">Keine Verbindung</span>}
          </div>
        </div>
      )}

      {meldung && (
        <div className={meldung.fehler ? 'hinweis hinweis--fehler' : 'hinweis hinweis--gut'}>{meldung.text}</div>
      )}

      {liste.length === 0 ? (
        <p className="gedaempft">Noch keine Datenblätter abgelegt.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Hersteller</th>
                <th style={{ textAlign: 'left' }}>Titel</th>
                <th style={{ textAlign: 'right' }}>Größe</th>
                <th style={{ textAlign: 'right' }}>Verknüpft</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {liste.map((d) => (
                <tr key={d.id}>
                  <td>{d.hersteller ?? '–'}</td>
                  <td>{d.titel}</td>
                  <td style={{ textAlign: 'right' }}>{groesse(d.groesse_bytes)}</td>
                  <td style={{ textAlign: 'right' }}>{verwendung.get(d.id) ?? 0}×</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="schlicht" onClick={() => void oeffne(d)}>
                      Öffnen
                    </button>
                    {darf && (
                      <button className="schlicht" onClick={() => void entferne(d)}>
                        Entfernen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Auswahl an einem Modul oder einer Komponente. Daneben „Öffnen“, damit man
 * aus der Zeile heraus nachsehen kann, ohne den Ordner zu suchen.
 */
export function Datenblattwahl({
  wert,
  darf,
  onAendern,
}: {
  wert: string | null | undefined;
  darf: boolean;
  onAendern: (id: string | null) => void;
}): React.JSX.Element {
  const liste = useDatenblaetter();
  const gewaehlt = liste.find((d) => d.id === wert);
  return (
    <span style={{ display: 'inline-flex', gap: '0.3rem', alignItems: 'center' }}>
      <select
        value={wert ?? ''}
        disabled={!darf}
        onChange={(e) => onAendern(e.target.value || null)}
        style={{ maxWidth: '14rem' }}
      >
        <option value="">— kein Datenblatt —</option>
        {liste.map((d) => (
          <option key={d.id} value={d.id}>
            {datenblattname(d)}
          </option>
        ))}
        {wert && !gewaehlt && <option value={wert}>(entfernt oder noch nicht geladen)</option>}
      </select>
      {gewaehlt && (
        <button
          className="schlicht"
          title={`${datenblattname(gewaehlt)} öffnen`}
          onClick={() => void oeffneDatenblatt(gewaehlt.id).catch((f) => window.alert(String(f)))}
        >
          Öffnen
        </button>
      )}
    </span>
  );
}
